// =============================================================================
// BILAN DE CLÔTURE - PDF final après terminaison (3 pages)
// Analyse finale après TERMINAISON du chantier (capitalisation)
// IMPORTANT: Tous les montants affichés sont en HT (Hors Taxes)
// =============================================================================

import {
  Document,
  Page,
  Text,
  View,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { sharedStyles, COLORS } from './pdfStyles';
import {
  formatMontant,
  formatMontantHT,
  formatDate,
  formatPct,
  calculerDuree,
  calculerVentilation,
  type PDFData,
} from './pdfUtils';
import { SEUILS_MARGE, SEUILS_RENTABILITE, getMargeConfig, BENCHMARKS_BTP } from '@/lib/calculs-rentabilite';

// =============================================================================
// PROPS
// =============================================================================

interface BilanCloturePDFProps {
  data: PDFData;
  entrepriseNom?: string;
  dateFinReelle?: string;
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function BilanCloturePDF({
  data,
  entrepriseNom = 'B8ild',
  dateFinReelle,
}: BilanCloturePDFProps) {
  const { chantier, devis, depenses, equipe, facturation, metriques, benchmarks, analyseIA, suspensions } = data;

  // Calculs
  const dateFinEffective = dateFinReelle || chantier.date_fin_reelle || chantier.date_fin_prevue;
  // Durée réelle = jours-homme / nb membres OU durée calendaire si pas de données équipe
  const dureeCalendaire = calculerDuree(chantier.date_debut, dateFinEffective);
  const dureeReelle = equipe.membres.length > 0 && equipe.total_jours > 0
    ? Math.round((equipe.total_jours / equipe.membres.length) * 10) / 10  // Arrondi à 1 décimale
    : dureeCalendaire;
  const ecartJours = Math.round((dureeReelle - chantier.duree_prevue) * 10) / 10;
  const margeConfig = getMargeConfig(metriques.marge_pct);
  const ventilation = calculerVentilation(depenses.total_mo, depenses.total_factures, depenses.total_frais);
  const dateGeneration = format(new Date(), "d MMMM yyyy", { locale: fr });

  // Calculs KPIs
  const caParJour = dureeReelle > 0 ? Math.round(devis.montant_ht / dureeReelle) : 0;
  const roi = depenses.total > 0 ? Math.round((metriques.marge_euros / depenses.total) * 100) : 0;
  const rentabiliteHoraire = equipe.total_heures > 0 ? Math.round(metriques.marge_euros / equipe.total_heures) : 0;
  const coutParPersonneParJour = equipe.membres.length > 0 && dureeReelle > 0
    ? Math.round(devis.montant_ht / dureeReelle / equipe.membres.length)
    : 0;

  // Points forts et améliorations basés sur les nouveaux seuils BTP
  const pointsForts: string[] = [];
  const pointsAmelioration: string[] = [];

  // Marge - utilisant les seuils BTP harmonisés (7 niveaux)
  if (metriques.marge_pct >= SEUILS_MARGE.EXCEPTIONNEL) {
    pointsForts.push(`Marge exceptionnelle de ${formatPct(metriques.marge_pct)}, top 10% du secteur BTP`);
  } else if (metriques.marge_pct >= SEUILS_MARGE.EXCELLENT) {
    pointsForts.push(`Excellente marge de ${formatPct(metriques.marge_pct)}, au-dessus de la moyenne BTP`);
  } else if (metriques.marge_pct >= SEUILS_MARGE.BON) {
    pointsForts.push(`Bonne marge de ${formatPct(metriques.marge_pct)}, dans la moyenne BTP`);
  } else if (metriques.marge_pct >= SEUILS_MARGE.CORRECT) {
    pointsAmelioration.push(`Marge de ${formatPct(metriques.marge_pct)} en dessous de la moyenne BTP (${BENCHMARKS_BTP.marge_min}-${BENCHMARKS_BTP.marge_moyenne}%)`);
  } else if (metriques.marge_pct >= 0) {
    pointsAmelioration.push(`Marge fragile de ${formatPct(metriques.marge_pct)}, optimisation urgente necessaire`);
  } else {
    pointsAmelioration.push(`Chantier a perte (${formatPct(metriques.marge_pct)}), analyse des causes requise`);
  }

  // Planning
  if (ecartJours <= 0) pointsForts.push(`Respect des delais (${chantier.duree_prevue}j prevus, ${dureeReelle}j realises)`);
  else if (ecartJours <= 2) pointsForts.push(`Leger depassement acceptable (+${ecartJours}j)`);
  else pointsAmelioration.push(`Retard de ${ecartJours} jours sur le planning`);

  if (equipe.cout_horaire_moyen > 0 && equipe.cout_horaire_moyen <= 22) {
    pointsForts.push(`Cout MO maitrise : ${equipe.cout_horaire_moyen}euro/h (benchmark : 22euro/h)`);
  }

  return (
    <Document>
      {/* ================================================================= */}
      {/* PAGE 1 : BILAN EXÉCUTIF                                           */}
      {/* ================================================================= */}
      <Page size="A4" style={sharedStyles.page}>
        {/* Header */}
        <View style={sharedStyles.header}>
          <View style={sharedStyles.headerLeft}>
            <Text style={sharedStyles.logoText}>B<Text style={{ color: '#F59E0B' }}>8</Text>ild</Text>
          </View>
          <View style={sharedStyles.headerRight}>
            <Text style={sharedStyles.headerTitle}>BILAN DE CLÔTURE</Text>
            <Text style={sharedStyles.headerSubtitle}>{dateGeneration}</Text>
          </View>
        </View>
        <View style={sharedStyles.bandeDore} />

        <View style={sharedStyles.content}>
          {/* Encart chantier terminé */}
          <View style={sharedStyles.chantierBox}>
            <Text style={sharedStyles.chantierNom}>{chantier.nom}</Text>
            <View style={sharedStyles.chantierInfoRow}>
              <Text style={sharedStyles.chantierLabel}>Client</Text>
              <Text style={sharedStyles.chantierValue}>{chantier.client || 'Non renseigné'}</Text>
            </View>
            <View style={sharedStyles.chantierInfoRow}>
              <Text style={sharedStyles.chantierLabel}>Adresse</Text>
              <Text style={sharedStyles.chantierValue}>{chantier.adresse || 'Non renseignée'}</Text>
            </View>
            <View style={sharedStyles.chantierInfoRow}>
              <Text style={sharedStyles.chantierLabel}>Période</Text>
              <Text style={sharedStyles.chantierValue}>
                {formatDate(chantier.date_debut)} - {formatDate(dateFinEffective)} ({dureeReelle} jours)
              </Text>
            </View>
          </View>

          {/* Encart MARGE REALISEE HT (gros et central) */}
          <View style={sharedStyles.margeFinaleBox}>
            <Text style={sharedStyles.margeFinaleLabel}>MARGE REALISEE (HT)</Text>
            <Text style={sharedStyles.margeFinaleValue}>{formatMontantHT(metriques.marge_euros)}</Text>
            <Text style={sharedStyles.margeFinalePct}>{formatPct(metriques.marge_pct)}</Text>
            <View style={[sharedStyles.margeFinaleStatus, { backgroundColor: margeConfig.bgColor }]}>
              <Text style={[sharedStyles.margeFinaleStatusText, { color: margeConfig.color }]}>
                {margeConfig.label}
              </Text>
            </View>
          </View>

          {/* Revenus vs Dépenses */}
          <View style={sharedStyles.statsRow}>
            {/* Revenus */}
            <View style={[sharedStyles.statsBox, { borderTopWidth: 3, borderTopColor: COLORS.vertSucces }]}>
              <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: COLORS.vertSucces, marginBottom: 10 }}>
                REVENUS
              </Text>
              <View style={{ width: '100%' }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>Devis HT</Text>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>{formatMontant(devis.montant_ht)}</Text>
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>Facturé</Text>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: facturation.total_facture === 0 ? COLORS.orangeAttention : COLORS.grisFonce }}>
                    {formatMontant(facturation.total_facture)}
                  </Text>
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>Encaissé</Text>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: facturation.total_paye === 0 ? COLORS.orangeAttention : COLORS.grisFonce }}>
                    {formatMontant(facturation.total_paye)}
                  </Text>
                </View>
              </View>
            </View>

            {/* Dépenses */}
            <View style={[sharedStyles.statsBox, { borderTopWidth: 3, borderTopColor: COLORS.rougeAlerte }]}>
              <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: COLORS.rougeAlerte, marginBottom: 10 }}>
                DÉPENSES
              </Text>
              <View style={{ width: '100%' }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>Matériaux</Text>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>{formatMontant(depenses.total_factures)}</Text>
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>Main d'oeuvre</Text>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>{formatMontant(depenses.total_mo)}</Text>
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>Frais</Text>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>{formatMontant(depenses.total_frais)}</Text>
                </View>
                <View style={{ height: 1, backgroundColor: COLORS.grisClair, marginVertical: 4 }} />
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: COLORS.rougeAlerte }}>TOTAL</Text>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: COLORS.rougeAlerte }}>{formatMontant(depenses.total)}</Text>
                </View>
              </View>
            </View>
          </View>

          <View style={sharedStyles.separator} />

          {/* Comparaison Prévu vs Réel */}
          <View style={sharedStyles.section}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>COMPARAISON PRÉVU vs RÉEL</Text>
            </View>

            <View style={sharedStyles.table}>
              <View style={sharedStyles.tableHeader}>
                <Text style={[sharedStyles.tableHeaderCell, { flex: 2 }]}>Indicateur</Text>
                <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Prévu</Text>
                <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Réel</Text>
                <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Écart</Text>
              </View>

              <View style={sharedStyles.tableRow}>
                <Text style={[sharedStyles.tableCell, { flex: 2 }]}>Durée (jours travaillés)</Text>
                <Text style={sharedStyles.tableCellRight}>{chantier.duree_prevue} jours</Text>
                <Text style={sharedStyles.tableCellRight}>{dureeReelle} jours</Text>
                <Text style={[sharedStyles.tableCellRight, ecartJours <= 0 ? sharedStyles.ecartPositif : sharedStyles.ecartNegatif]}>
                  {ecartJours > 0 ? '+' : ''}{ecartJours}j
                </Text>
              </View>

              <View style={[sharedStyles.tableRow, sharedStyles.tableRowAlt]}>
                <Text style={[sharedStyles.tableCell, { flex: 2 }]}>Marge HT</Text>
                <Text style={sharedStyles.tableCellRight}>Objectif {SEUILS_MARGE.BON}-{SEUILS_MARGE.EXCELLENT}%</Text>
                <Text style={sharedStyles.tableCellRight}>{formatPct(metriques.marge_pct)}</Text>
                <Text style={[sharedStyles.tableCellRight, metriques.marge_pct >= SEUILS_MARGE.EXCELLENT ? sharedStyles.ecartPositif : metriques.marge_pct >= SEUILS_MARGE.BON ? sharedStyles.ecartNeutre : sharedStyles.ecartNegatif]}>
                  {metriques.marge_pct >= SEUILS_MARGE.EXCELLENT ? 'Au-dessus' : metriques.marge_pct >= SEUILS_MARGE.BON ? 'Moyenne' : 'En dessous'}
                </Text>
              </View>

              {/* Afficher les jours de retard si présents */}
              {metriques.jours_retard_travailles && metriques.jours_retard_travailles > 0 && (
                <View style={sharedStyles.tableRow}>
                  <Text style={[sharedStyles.tableCell, { flex: 2 }]}>Jours de retard travaillés</Text>
                  <Text style={sharedStyles.tableCellRight}>-</Text>
                  <Text style={[sharedStyles.tableCellRight, { color: COLORS.rougeAlerte }]}>
                    {metriques.jours_retard_travailles} jours
                  </Text>
                  <Text style={[sharedStyles.tableCellRight, { color: COLORS.rougeAlerte }]}>
                    {formatMontant(metriques.cout_retard || 0)}
                  </Text>
                </View>
              )}

              {/* Afficher les jours de reprise si présents */}
              {metriques.jours_reprise_travailles && metriques.jours_reprise_travailles > 0 && (
                <View style={sharedStyles.tableRow}>
                  <Text style={[sharedStyles.tableCell, { flex: 2 }]}>Jours de reprise travaillés</Text>
                  <Text style={sharedStyles.tableCellRight}>-</Text>
                  <Text style={[sharedStyles.tableCellRight, { color: COLORS.bleuRoi }]}>
                    {metriques.jours_reprise_travailles} jours
                  </Text>
                  <Text style={[sharedStyles.tableCellRight, { color: COLORS.bleuRoi }]}>
                    {formatMontant(metriques.cout_reprise || 0)}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Footer - fixed pour éviter le débordement sur une nouvelle page */}
        <View style={sharedStyles.footer} fixed>
          <View style={sharedStyles.footerBande} />
          <View style={sharedStyles.footerContent}>
            <Text style={sharedStyles.footerText}>
              B8ild - {entrepriseNom} - Bilan généré le {dateGeneration}
            </Text>
            <Text style={sharedStyles.footerPage}>Page 1/3</Text>
          </View>
        </View>
      </Page>

      {/* ================================================================= */}
      {/* PAGE 2 : DÉTAIL FINANCIER                                         */}
      {/* ================================================================= */}
      <Page size="A4" style={sharedStyles.page}>
        {/* Header */}
        <View style={sharedStyles.header}>
          <View style={sharedStyles.headerLeft}>
            <Text style={sharedStyles.logoText}>B<Text style={{ color: '#F59E0B' }}>8</Text>ild</Text>
          </View>
          <View style={sharedStyles.headerRight}>
            <Text style={sharedStyles.headerTitle}>DÉTAIL FINANCIER</Text>
            <Text style={sharedStyles.headerSubtitle}>{chantier.nom}</Text>
          </View>
        </View>
        <View style={sharedStyles.bandeDore} />

        <View style={sharedStyles.content}>
          {/* Ventilation des coûts */}
          <View style={sharedStyles.section}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>VENTILATION DES COÛTS</Text>
            </View>

            {/* Barres de ventilation */}
            <View style={{ marginBottom: 15 }}>
              <View style={sharedStyles.ventilationBar}>
                <Text style={sharedStyles.ventilationLabel}>Main d'oeuvre</Text>
                <View style={sharedStyles.ventilationBarContainer}>
                  <View style={[sharedStyles.ventilationBarFill, { width: `${ventilation.mo}%`, backgroundColor: COLORS.bleuRoi }]} />
                </View>
                <Text style={sharedStyles.ventilationValue}>{formatMontant(depenses.total_mo)}</Text>
                <Text style={sharedStyles.ventilationPct}>{ventilation.mo}%</Text>
              </View>

              <View style={sharedStyles.ventilationBar}>
                <Text style={sharedStyles.ventilationLabel}>Matériaux</Text>
                <View style={sharedStyles.ventilationBarContainer}>
                  <View style={[sharedStyles.ventilationBarFill, { width: `${ventilation.materiaux}%`, backgroundColor: COLORS.dore }]} />
                </View>
                <Text style={sharedStyles.ventilationValue}>{formatMontant(depenses.total_factures)}</Text>
                <Text style={sharedStyles.ventilationPct}>{ventilation.materiaux}%</Text>
              </View>

              <View style={sharedStyles.ventilationBar}>
                <Text style={sharedStyles.ventilationLabel}>Frais</Text>
                <View style={sharedStyles.ventilationBarContainer}>
                  <View style={[sharedStyles.ventilationBarFill, { width: `${ventilation.frais}%`, backgroundColor: COLORS.grisTexte }]} />
                </View>
                <Text style={sharedStyles.ventilationValue}>{formatMontant(depenses.total_frais)}</Text>
                <Text style={sharedStyles.ventilationPct}>{ventilation.frais}%</Text>
              </View>
            </View>
          </View>

          <View style={[sharedStyles.separator, { marginVertical: 10 }]} />

          {/* Détail Main d'oeuvre */}
          <View style={sharedStyles.section}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>DÉTAIL MAIN D'OEUVRE</Text>
            </View>

            <View style={sharedStyles.table}>
              <View style={sharedStyles.tableHeader}>
                <Text style={[sharedStyles.tableHeaderCell, { flex: 2 }]}>Membre</Text>
                <Text style={[sharedStyles.tableHeaderCell, { flex: 1.2 }]}>Poste</Text>
                <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Jours</Text>
                <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Heures</Text>
                <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Coût</Text>
              </View>

              {equipe.membres.length > 0 ? (
                <>
                  {equipe.membres.map((membre, index) => (
                    <View
                      key={index}
                      style={[sharedStyles.tableRow, index % 2 === 1 ? sharedStyles.tableRowAlt : {}]}
                    >
                      <Text style={[sharedStyles.tableCell, { flex: 2 }]}>{membre.nom}</Text>
                      <Text style={[sharedStyles.tableCell, { flex: 1.2 }]}>{membre.poste}</Text>
                      <Text style={sharedStyles.tableCellRight}>{membre.jours}j</Text>
                      <Text style={sharedStyles.tableCellRight}>{membre.heures}h</Text>
                      <Text style={sharedStyles.tableCellRight}>{formatMontant(membre.cout)}</Text>
                    </View>
                  ))}
                  <View style={[sharedStyles.tableRow, sharedStyles.tableRowTotal]}>
                    <Text style={[sharedStyles.tableCell, sharedStyles.tableCellBold, { flex: 2 }]}>TOTAL</Text>
                    <Text style={[sharedStyles.tableCell, { flex: 1.2 }]}>{equipe.membres.length} pers.</Text>
                    <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>{equipe.total_jours}j</Text>
                    <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>{equipe.total_heures}h</Text>
                    <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>{formatMontant(equipe.total_cout)}</Text>
                  </View>
                </>
              ) : (
                <View style={sharedStyles.tableRow}>
                  <Text style={{ flex: 1, textAlign: 'center', color: COLORS.grisTexte }}>
                    Aucune donnée équipe disponible
                  </Text>
                </View>
              )}
            </View>

            {equipe.cout_horaire_moyen > 0 && (
              <View style={{ flexDirection: 'row', marginTop: 10, gap: 15 }}>
                <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>
                  Coût horaire moyen : <Text style={{ fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi }}>{equipe.cout_horaire_moyen}€/h</Text>
                </Text>
                <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>
                  Productivité : <Text style={{ fontFamily: 'Helvetica-Bold', color: equipe.cout_horaire_moyen <= 22 ? COLORS.vertSucces : COLORS.orangeAttention }}>
                    {equipe.cout_horaire_moyen <= 22 ? 'Bonne' : 'À surveiller'} (benchmark : 22€/h)
                  </Text>
                </Text>
              </View>
            )}
          </View>

          <View style={[sharedStyles.separator, { marginVertical: 10 }]} />

          {/* Détail Achats */}
          <View style={sharedStyles.section}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>DÉTAIL ACHATS</Text>
            </View>

            {/* Factures fournisseurs */}
            {depenses.factures.length > 0 && (
              <>
                <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi, marginBottom: 8 }}>
                  FACTURES FOURNISSEURS ({depenses.factures.length})
                </Text>
                <View style={sharedStyles.table}>
                  <View style={sharedStyles.tableHeader}>
                    <Text style={[sharedStyles.tableHeaderCell, { flex: 2 }]}>Fournisseur</Text>
                    <Text style={[sharedStyles.tableHeaderCell, { flex: 1.5 }]}>Catégorie</Text>
                    <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Montant HT</Text>
                  </View>

                  {depenses.factures.map((facture, index) => (
                    <View
                      key={index}
                      style={[sharedStyles.tableRow, index % 2 === 1 ? sharedStyles.tableRowAlt : {}]}
                    >
                      <Text style={[sharedStyles.tableCell, { flex: 2 }]}>{facture.fournisseur}</Text>
                      <Text style={[sharedStyles.tableCell, { flex: 1.5 }]}>{facture.categorie}</Text>
                      <Text style={sharedStyles.tableCellRight}>{formatMontant(facture.montant_ht)}</Text>
                    </View>
                  ))}

                  <View style={[sharedStyles.tableRow, sharedStyles.tableRowTotal]}>
                    <Text style={[sharedStyles.tableCell, sharedStyles.tableCellBold, { flex: 3.5 }]}>SOUS-TOTAL FACTURES</Text>
                    <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>{formatMontant(depenses.total_factures)}</Text>
                  </View>
                </View>
              </>
            )}

            {/* Frais annexes + Total achats — groupés pour éviter coupure de page */}
            <View wrap={false}>
              {depenses.frais.length > 0 && (
                <>
                  <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi, marginBottom: 8, marginTop: 4 }}>
                    FRAIS ANNEXES ({depenses.frais.length})
                  </Text>
                  <View style={sharedStyles.table}>
                    <View style={sharedStyles.tableHeader}>
                      <Text style={[sharedStyles.tableHeaderCell, { flex: 1.5 }]}>Type</Text>
                      <Text style={[sharedStyles.tableHeaderCell, { flex: 2 }]}>Description</Text>
                      <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Montant</Text>
                    </View>

                    {depenses.frais.map((frais, index) => (
                      <View
                        key={index}
                        style={[sharedStyles.tableRow, index % 2 === 1 ? sharedStyles.tableRowAlt : {}]}
                      >
                        <Text style={[sharedStyles.tableCell, { flex: 1.5 }]}>{frais.type}</Text>
                        <Text style={[sharedStyles.tableCell, { flex: 2 }]}>{frais.description || '-'}</Text>
                        <Text style={sharedStyles.tableCellRight}>{formatMontant(frais.montant)}</Text>
                      </View>
                    ))}

                    <View style={[sharedStyles.tableRow, sharedStyles.tableRowTotal]}>
                      <Text style={[sharedStyles.tableCell, sharedStyles.tableCellBold, { flex: 3.5 }]}>SOUS-TOTAL FRAIS</Text>
                      <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>{formatMontant(depenses.total_frais)}</Text>
                    </View>
                  </View>
                </>
              )}

              {/* Total achats */}
              <View style={{ backgroundColor: COLORS.bleuClair, borderRadius: 6, padding: 10, marginTop: 4, flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi }}>TOTAL ACHATS ET FRAIS</Text>
                <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi }}>{formatMontant(depenses.total_factures + depenses.total_frais)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Footer - fixed pour éviter le débordement sur une nouvelle page */}
        <View style={sharedStyles.footer} fixed>
          <View style={sharedStyles.footerBande} />
          <View style={sharedStyles.footerContent}>
            <Text style={sharedStyles.footerText}>
              B8ild - {entrepriseNom} - Bilan généré le {dateGeneration}
            </Text>
            <Text style={sharedStyles.footerPage}>Page 2/3</Text>
          </View>
        </View>
      </Page>

      {/* ================================================================= */}
      {/* PAGE 3 : ANALYSE ET ENSEIGNEMENTS                                 */}
      {/* ================================================================= */}
      <Page size="A4" style={sharedStyles.page}>
        {/* Header */}
        <View style={sharedStyles.header}>
          <View style={sharedStyles.headerLeft}>
            <Text style={sharedStyles.logoText}>B<Text style={{ color: '#F59E0B' }}>8</Text>ild</Text>
          </View>
          <View style={sharedStyles.headerRight}>
            <Text style={sharedStyles.headerTitle}>ANALYSE & ENSEIGNEMENTS</Text>
            <Text style={sharedStyles.headerSubtitle}>{chantier.nom}</Text>
          </View>
        </View>
        <View style={sharedStyles.bandeDore} />

        <View style={sharedStyles.content}>
          {/* KPIs de performance */}
          <View style={sharedStyles.section}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>INDICATEURS DE PERFORMANCE (KPIs)</Text>
            </View>

            <View style={sharedStyles.statsRow}>
              <View style={[sharedStyles.statsBox, { borderTopWidth: 3, borderTopColor: COLORS.bleuRoi }]}>
                <Text style={[sharedStyles.statsValue, { color: metriques.marge_pct >= SEUILS_MARGE.BON ? COLORS.vertSucces : metriques.marge_pct >= SEUILS_MARGE.CORRECT ? COLORS.orangeAttention : COLORS.rougeAlerte }]}>
                  {formatPct(metriques.marge_pct)}
                </Text>
                <Text style={sharedStyles.statsLabel}>Marge HT</Text>
                <Text style={{ fontSize: 8, color: metriques.marge_pct >= SEUILS_MARGE.BON ? COLORS.vertSucces : COLORS.orangeAttention, marginTop: 4 }}>
                  {metriques.marge_pct >= SEUILS_MARGE.BON ? '+' : '!'}
                </Text>
              </View>
              <View style={[sharedStyles.statsBox, { borderTopWidth: 3, borderTopColor: COLORS.bleuRoi }]}>
                <Text style={sharedStyles.statsValue}>{roi}%</Text>
                <Text style={sharedStyles.statsLabel}>ROI</Text>
                <Text style={{ fontSize: 8, color: roi >= 50 ? COLORS.vertSucces : COLORS.orangeAttention, marginTop: 4 }}>
                  {roi >= 50 ? '+' : '-'}
                </Text>
              </View>
              <View style={[sharedStyles.statsBox, { borderTopWidth: 3, borderTopColor: COLORS.bleuRoi }]}>
                <Text style={[sharedStyles.statsValue, { fontSize: 16 }]}>{formatMontant(caParJour)}</Text>
                <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi }}>HT</Text>
                <Text style={sharedStyles.statsLabel}>CA/JOUR</Text>
                <Text style={{ fontSize: 8, color: COLORS.vertSucces, marginTop: 4 }}>+</Text>
              </View>
              <View style={[sharedStyles.statsBox, { borderTopWidth: 3, borderTopColor: COLORS.bleuRoi }]}>
                <Text style={sharedStyles.statsValue}>{rentabiliteHoraire > 0 ? `${rentabiliteHoraire}€/h` : 'N/A'}</Text>
                <Text style={sharedStyles.statsLabel}>Rent./h</Text>
                <Text style={{ fontSize: 8, color: rentabiliteHoraire > 15 ? COLORS.vertSucces : COLORS.orangeAttention, marginTop: 4 }}>
                  {rentabiliteHoraire > 15 ? '+' : '-'}
                </Text>
              </View>
            </View>

            {/* Comparaison benchmarks */}
            <View style={{ backgroundColor: COLORS.grisClair, borderRadius: 8, padding: 12, marginTop: 10 }}>
              <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi, marginBottom: 6 }}>
                Comparaison aux benchmarks BTP :
              </Text>
              <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>
                - Marge moyenne secteur : {benchmarks.marge_min}-{benchmarks.marge_max}%
              </Text>
              <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>
                - Votre marge : {formatPct(metriques.marge_pct)} - {' '}
                <Text style={{ color: metriques.marge_pct >= benchmarks.marge_moyenne ? COLORS.vertSucces : COLORS.orangeAttention, fontFamily: 'Helvetica-Bold' }}>
                  {metriques.marge_pct >= benchmarks.marge_max ? 'Au-dessus' : metriques.marge_pct >= benchmarks.marge_min ? 'Dans la moyenne' : 'En dessous'}
                  {metriques.marge_pct >= benchmarks.marge_moyenne && ` (+${Math.round(metriques.marge_pct - benchmarks.marge_moyenne)} pts)`}
                </Text>
              </Text>
            </View>
          </View>

          <View style={sharedStyles.separator} />

          {/* Points forts */}
          {pointsForts.length > 0 && (
            <View style={sharedStyles.section}>
              <View style={sharedStyles.sectionHeader}>
                <Text style={sharedStyles.sectionTitle}>POINTS FORTS</Text>
              </View>

              {pointsForts.map((point, index) => (
                <View key={index} style={[sharedStyles.alertBox, sharedStyles.alertSuccess]}>
                  <Text style={sharedStyles.alertText}>- {point}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Points d'amélioration */}
          {pointsAmelioration.length > 0 && (
            <View style={sharedStyles.section}>
              <View style={sharedStyles.sectionHeader}>
                <Text style={sharedStyles.sectionTitle}>POINTS D'AMÉLIORATION</Text>
              </View>

              {pointsAmelioration.map((point, index) => (
                <View key={index} style={[sharedStyles.alertBox, sharedStyles.alertWarning]}>
                  <Text style={sharedStyles.alertText}>- {point}</Text>
                  {point.includes('facturation') && (
                    <Text style={{ fontSize: 9, color: COLORS.orangeAttention, fontFamily: 'Helvetica-Bold', marginTop: 4 }}>
                      - ACTION : Émettre la facture immédiatement
                    </Text>
                  )}
                </View>
              ))}
            </View>
          )}

          <View style={sharedStyles.separator} />

          {/* Enseignements */}
          <View style={sharedStyles.section}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>ENSEIGNEMENTS POUR LES PROCHAINS CHANTIERS</Text>
            </View>

            <View style={sharedStyles.enseignementBox}>
              <Text style={sharedStyles.enseignementTitle}>Ce chantier comme reference</Text>
              <Text style={sharedStyles.enseignementText}>
                Ce chantier avec {equipe.membres.length} {equipe.membres.length > 1 ? 'personnes' : 'personne'} sur {dureeReelle} jours
                a genere une marge de {formatPct(metriques.marge_pct)}. Cette configuration peut servir de reference pour les chantiers similaires.
              </Text>
              <Text style={sharedStyles.enseignementRatio}>
                Ratio a retenir : {formatMontantHT(devis.montant_ht)} / {dureeReelle}j / {equipe.membres.length} pers. = {formatMontantHT(coutParPersonneParJour)}/jour/pers
              </Text>
              <Text style={sharedStyles.enseignementRatio}>
                Cout journalier equipe : {formatMontantHT(metriques.cout_journalier)}
              </Text>
            </View>
          </View>

          {/* Historique des suspensions (si applicable) */}
          {suspensions && suspensions.nb_suspensions > 0 && (
            <View style={sharedStyles.section}>
              <View style={sharedStyles.sectionHeader}>
                <Text style={sharedStyles.sectionTitle}>HISTORIQUE DES SUSPENSIONS</Text>
              </View>

              <View style={{ backgroundColor: COLORS.orangeAttention + '15', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <Text style={{ fontSize: 9, color: COLORS.orangeAttention }}>
                  Ce chantier a été suspendu {suspensions.nb_suspensions} fois pour un total de {suspensions.total_jours} jour{suspensions.total_jours > 1 ? 's' : ''}.
                </Text>
              </View>

              {suspensions.suspensions.filter(s => s.date_reprise).map((suspension, index) => (
                <View key={index} style={{ marginBottom: 8, paddingLeft: 10, borderLeftWidth: 2, borderLeftColor: COLORS.orangeAttention }}>
                  <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: COLORS.grisFonce }}>
                    {formatDate(suspension.date_suspension)} - {formatDate(suspension.date_reprise!)} ({suspension.duree_jours} jour{suspension.duree_jours > 1 ? 's' : ''})
                  </Text>
                  <Text style={{ fontSize: 8, color: COLORS.grisTexte, marginTop: 2 }}>
                    Raison : {suspension.raison} {suspension.type_suspension === 'partielle' ? '(partielle)' : ''}
                  </Text>
                </View>
              ))}

            </View>
          )}

          {/* Documents associés */}
          <View style={sharedStyles.section} wrap={false}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>DOCUMENTS ASSOCIÉS</Text>
            </View>

            <View style={sharedStyles.statsRow}>
              <View style={[sharedStyles.statsBox, { flex: 1 }]}>
                <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi }}>1</Text>
                <Text style={{ fontSize: 8, color: COLORS.grisTexte, textAlign: 'center' }}>Devis{'\n'}({devis.version} - {devis.statut})</Text>
              </View>
              <View style={[sharedStyles.statsBox, { flex: 1 }]}>
                <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi }}>{depenses.factures.length}</Text>
                <Text style={{ fontSize: 8, color: COLORS.grisTexte, textAlign: 'center' }}>Factures{'\n'}fournisseurs</Text>
              </View>
              <View style={[sharedStyles.statsBox, { flex: 1 }]}>
                <Text style={{ fontSize: 16, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi }}>{depenses.frais.length}</Text>
                <Text style={{ fontSize: 8, color: COLORS.grisTexte, textAlign: 'center' }}>Frais{'\n'}annexes</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Footer avec citation - fixed pour éviter le débordement sur une nouvelle page */}
        <View style={sharedStyles.footer} fixed>
          <View style={sharedStyles.footerBande} />
          <View style={sharedStyles.footerContent}>
            <Text style={sharedStyles.footerQuote}>
              "Piloter sa rentabilité, c'est maîtriser son avenir."
            </Text>
            <Text style={sharedStyles.footerPage}>Page 3/3</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}

export default BilanCloturePDF;
